import React, { useState, useMemo, useEffect } from 'react';
import LayoutHome from '../../components/layout/LayoutHome';
import {
    Input, Button, Table, Tag, Typography, Row, Col, Modal, Form, InputNumber, Space, message, Divider,
    Grid, Card, DatePicker, List, Radio, Alert, ConfigProvider, Select, Tooltip
} from 'antd';
import {
    EditOutlined, ThunderboltOutlined, FileTextOutlined, HistoryOutlined, UserOutlined,
    RightOutlined, DownOutlined, ArrowRightOutlined, ShopOutlined, FilterOutlined, SearchOutlined, QuestionCircleOutlined
} from '@ant-design/icons';
import { useGetProductos } from '../../hooks/useProductos';
import { useAjustarInventario, useGetHistorialInventario } from '../../hooks/useInventario';
import { useGetBodegas, useGetStockBodega } from '../../hooks/useBodegas';
import { useConfig } from '../../context/ConfigContext';
import dayjs from 'dayjs';
import TabBodegas from './sections/TabBodegas';
import es_ES from 'antd/locale/es_ES';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const Inventario = () => {
    const screens = useBreakpoint();
    const isMobile = screens.md === false;

    const { data: productosDB = [], isLoading: cargandoProductos } = useGetProductos();
    const { data: bodegasDB = [] } = useGetBodegas();

    // NUEVO ESTADO: Controla qué bodega estamos viendo
    const [bodegaActiva, setBodegaActiva] = useState('global');
    const { data: stockBodegaDB = [], isFetching: cargandoStockBodega } = useGetStockBodega(bodegaActiva);

    const ajustarInventario = useAjustarInventario();
    const { config } = useConfig();
    const isPremium = config?.plan_actual === 'premium';

    const bloquearAjusteEnGlobal = isPremium && bodegasDB.length > 1 && bodegaActiva === 'global';
    const nombreBodegaAjuste = bodegaActiva === 'global'
        ? (bodegasDB.find(b => b.es_default)?.nombre || 'Bodega Principal')
        : (bodegasDB.find(b => b.id === bodegaActiva)?.nombre || 'Bodega seleccionada');

    const [seccionActual, setSeccionActual] = useState('principal');
    const [busqueda, setBusqueda] = useState('');

    const [itemAjuste, setItemAjuste] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [previewStock, setPreviewStock] = useState(0);

    const [mostrarFiltros, setMostrarFiltros] = useState(false);

    const [fechaDesde, setFechaDesde] = useState(dayjs().startOf('month'));
    const [fechaHasta, setFechaHasta] = useState(dayjs().endOf('day'));

    const inicioStr = fechaDesde.format('YYYY-MM-DD');
    const finStr = fechaHasta.format('YYYY-MM-DD');

    const { data: historialDB = [], isLoading: cargandoHistorial } = useGetHistorialInventario(inicioStr, finStr);

    // Y el useMemo ahora solo filtra por el TIPO de movimiento
    const historialSoloManual = useMemo(() => {
        return historialDB.filter(h => h.tipo_movimiento !== 'venta' && h.tipo_movimiento !== 'traslado');
    }, [historialDB]);

    const [form] = Form.useForm();

    useEffect(() => {
        if (itemAjuste && isModalOpen) {
            form.setFieldsValue({
                tipo_ajuste: 'agregar',
                cantidad_ajuste: 0,
                nuevo_precio_compra: itemAjuste.precio_compra,
                nuevo_precio_venta: itemAjuste.isVariant ? itemAjuste.precio_base : itemAjuste.precio_venta,
                observaciones: ''
            });
            setPreviewStock(itemAjuste.stock);
        }
    }, [itemAjuste, isModalOpen, form]);

    const handleFormValuesChange = (changedValues, allValues) => {
        if (changedValues.tipo_ajuste || changedValues.cantidad_ajuste !== undefined) {
            const actual = itemAjuste?.stock || 0;
            const cantidad = Number(allValues.cantidad_ajuste) || 0;
            if (allValues.tipo_ajuste === 'agregar') setPreviewStock(actual + cantidad);
            else if (allValues.tipo_ajuste === 'restar') setPreviewStock(Math.max(0, actual - cantidad));
            else setPreviewStock(cantidad);
        }
    };

    // =========================================================================
    // MAGIA DE BODEGAS: Cruzamos los productos base con el stock de la bodega
    // =========================================================================
    const productosConStockReal = useMemo(() => {
        if (!stockBodegaDB) return productosDB;

        // Verificamos si estamos en la vista Global o en la Bodega Principal (Centro de Distribución)
        const bodegaSeleccionadaObj = bodegasDB.find(b => b.id === bodegaActiva);
        const mostrarTodoElCatalogo = bodegaActiva === 'global' || bodegaSeleccionadaObj?.es_default === true;

        const productosFiltrados = [];

        productosDB.forEach(p => {
            // === 1. LÓGICA DE COMBOS ===
            if (p.es_combo) {
                let maxArmables = Infinity;
                let ingredientesEnBodega = false;

                if (p.combo_items && p.combo_items.length > 0) {
                    p.combo_items.forEach(ingrediente => {
                        const registrosStock = stockBodegaDB.filter(sb =>
                            sb.producto_id === ingrediente.producto_id &&
                            (ingrediente.variante_id ? sb.variante_id === ingrediente.variante_id : !sb.variante_id)
                        );

                        if (registrosStock.length > 0) {
                            ingredientesEnBodega = true;
                        }

                        const stockFisico = registrosStock.reduce((sum, r) => sum + Number(r.stock), 0);
                        const posibles = Math.floor(stockFisico / Number(ingrediente.cantidad));

                        if (posibles < maxArmables) {
                            maxArmables = posibles;
                        }
                    });
                } else {
                    maxArmables = 0;
                }

                if (maxArmables === Infinity) maxArmables = 0;

                if (mostrarTodoElCatalogo || maxArmables > 0 || ingredientesEnBodega) {
                    productosFiltrados.push({ ...p, stock: maxArmables });
                }
                return;
            }

            // === 2. LÓGICA DE VARIANTES ===
            if (p.has_variants && p.variantes) {
                const variantesConStock = p.variantes.map(v => {
                    const registros = stockBodegaDB.filter(sb => sb.producto_id === p.id && sb.variante_id === v.id);
                    const stockVar = registros.reduce((sum, r) => sum + Number(r.stock), 0);
                    return {
                        ...v,
                        stock: stockVar,
                        existeEnBodega: registros.length > 0
                    };
                }).filter(v => mostrarTodoElCatalogo || v.existeEnBodega);

                if (variantesConStock.length > 0) {
                    const stockTotalPadre = variantesConStock.reduce((sum, v) => sum + Number(v.stock), 0);
                    productosFiltrados.push({ ...p, stock: stockTotalPadre, variantes: variantesConStock });
                }
            } else {
                // === 3. LÓGICA DE SIMPLES ===
                const registros = stockBodegaDB.filter(sb => sb.producto_id === p.id && !sb.variante_id);
                const stockFisico = registros.reduce((sum, r) => sum + Number(r.stock), 0);
                const existeEnBodega = registros.length > 0;

                if (mostrarTodoElCatalogo || existeEnBodega) {
                    productosFiltrados.push({ ...p, stock: stockFisico });
                }
            }
        });

        return productosFiltrados;
    }, [productosDB, stockBodegaDB, bodegaActiva, bodegasDB]);

    // Apuntamos los KPIs a la nueva variable `productosConStockReal`
    const statsGlobales = useMemo(() => {
        let costoTotal = 0;
        let ventaTotal = 0;
        let stockTotal = 0;

        productosConStockReal.forEach(p => {
            if (p.es_combo) return;

            if (p.has_variants && p.variantes) {
                p.variantes.forEach(v => {
                    const stock = Number(v.stock) || 0;
                    stockTotal += stock;
                    costoTotal += (stock * (Number(v.precio_compra) || 0));
                    ventaTotal += (stock * (Number(v.precio_base) || 0));
                });
            } else {
                const stock = Number(p.stock) || 0;
                stockTotal += stock;
                costoTotal += (stock * (Number(p.precio_compra) || 0));
                ventaTotal += (stock * (Number(p.precio_venta) || 0));
            }
        });
        const gananciaBruta = ventaTotal - costoTotal;
        const margenPorcentaje = ventaTotal > 0 ? (gananciaBruta / ventaTotal) * 100 : 0;
        return { refs: productosConStockReal.filter(p => !p.es_combo).length, costoTotal, margenPorcentaje, stockTotal, gananciaBruta };
    }, [productosConStockReal]);

    // Apuntamos el filtro de búsqueda a la nueva variable `productosConStockReal`
    const productosFiltrados = useMemo(() => {
        if (!busqueda.trim()) return productosConStockReal;
        const lowerBusqueda = busqueda.toLowerCase();

        return productosConStockReal.filter(p => {
            const matchPadre = p.nombre.toLowerCase().includes(lowerBusqueda) ||
                p.codigo_barras?.toString().includes(busqueda) ||
                p.codigo_interno?.toString().includes(busqueda);

            const matchVariante = p.has_variants && p.variantes?.some(v =>
                v.codigo_barras?.toString().includes(busqueda) ||
                v.codigo_interno?.toString().includes(busqueda) ||
                v.atributos?.nombre?.toLowerCase().includes(lowerBusqueda)
            );
            return matchPadre || matchVariante;
        });
    }, [productosConStockReal, busqueda]);

    const columnasWeb = [
        {
            title: 'PRODUCTO',
            dataIndex: 'nombre',
            width: '45%',
            render: (t, r) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Text strong style={{ color: '#0f172a', fontSize: '14px' }}>{t?.toUpperCase()}</Text>
                    {r.has_variants && <Tag bordered={false} style={{ marginLeft: 10, fontSize: '10px', background: '#e0f2fe', color: '#0284c7', fontWeight: 'bold' }}>{r.variantes?.length || 0} Variantes</Tag>}
                    {r.es_combo && <Tag bordered={false} color="purple" style={{ marginLeft: 10, fontSize: '10px', fontWeight: 'bold' }}>Combo</Tag>}
                </div>
            )
        },
        {
            title: 'COSTO COMPRA',
            dataIndex: 'precio_compra',
            width: '15%',
            align: 'left',
            render: (v, r) => r.has_variants ? <Text type="secondary" style={{ fontSize: '12px' }}>Múltiples</Text> : <Text type="secondary">$ {Number(v).toLocaleString('es-CO')}</Text>
        },
        {
            title: 'PRECIO VENTA',
            dataIndex: 'precio_venta',
            width: '15%',
            align: 'left',
            render: (v, r) => r.has_variants ? <Text type="secondary" style={{ fontSize: '12px' }}>Múltiples</Text> : <Text strong>$ {Number(v).toLocaleString('es-CO')}</Text>
        },
        {
            title: 'STOCK',
            dataIndex: 'stock',
            width: '10%',
            align: 'center',
            render: (v, r) => {
                if (r.es_combo) {
                    return v > 0
                        ? <Tag color="purple" style={{ fontWeight: '800', borderRadius: '6px', padding: '0 12px' }}>Dinámico</Tag>
                        : <Tag color="red" style={{ fontWeight: '800', borderRadius: '6px', padding: '0 12px' }}>Agotado</Tag>;
                }
                const stockReal = r.has_variants && r.variantes ? r.variantes.reduce((acum, vari) => acum + Number(vari.stock || 0), 0) : Number(v || 0);
                return <Tag color={stockReal <= (r.stock_minimo || 5) ? 'red' : 'blue'} style={{ fontWeight: '800', borderRadius: '6px', padding: '0 12px' }}>{stockReal} {r.has_variants && <span style={{ fontSize: '10px', marginLeft: 4, fontWeight: 'normal' }}>(Total)</span>}</Tag>;
            }
        },
        {
            title: 'ACCIONES',
            width: '15%',
            align: 'center',
            render: (_, r) => (r.has_variants || r.es_combo) ? null : (
                <Tooltip title={bloquearAjusteEnGlobal ? 'Selecciona una bodega específica arriba para ajustar su stock físico' : 'Ajustar stock y precios'}>
                    <Button
                        type="primary" ghost icon={<EditOutlined />}
                        disabled={bloquearAjusteEnGlobal}
                        onClick={() => { setItemAjuste(r); setIsModalOpen(true); }}
                        style={{ borderRadius: '8px' }}
                    >
                        Ajustar
                    </Button>
                </Tooltip>
            )
        }
    ];

    const expandedRowRender = (record) => {
        if (!record.has_variants || !record.variantes) return null;
        return (
            <div style={{ padding: '8px 45px 8px 8px', background: '#f8fafc', borderLeft: '3px solid #bae6fd' }}>
                <Table
                    dataSource={record.variantes}
                    pagination={false}
                    rowKey="id"
                    size="small"
                    style={{ background: 'transparent' }}
                    columns={[
                        { title: '↳ PRESENTACIÓN', dataIndex: ['atributos', 'nombre'], render: t => <Text style={{ color: '#334155', fontWeight: '600' }}>{t}</Text> },
                        { title: 'COSTO COMPRA', dataIndex: 'precio_compra', align: 'left', render: v => <Text type="secondary">$ {Number(v || 0).toLocaleString('es-CO')}</Text> },
                        { title: 'PRECIO VENTA', dataIndex: 'precio_base', align: 'left', render: v => <Text strong style={{ color: '#334155' }}>$ {Number(v || 0).toLocaleString('es-CO')}</Text> },
                        { title: 'STOCK', dataIndex: 'stock', align: 'center', render: v => <Tag color={v <= 0 ? 'red' : 'default'} style={{ fontWeight: v <= 0 ? 'bold' : 'normal', border: 'none', background: v <= 0 ? '#fee2e2' : '#f1f5f9', color: v <= 0 ? '#ef4444' : '#475569' }}>{v}</Tag> },
                        {
                            title: 'ACCIONES',
                            align: 'center',
                            render: (_, varRecord) => (
                                <Tooltip title={bloquearAjusteEnGlobal ? 'Selecciona una bodega específica arriba para ajustar su stock' : 'Ajustar variante'}>
                                    <Button size="small" type="link" icon={<EditOutlined />}
                                        disabled={bloquearAjusteEnGlobal}
                                        onClick={() => { setItemAjuste({ ...varRecord, isVariant: true, producto_id: record.id, nombrePadre: record.nombre }); setIsModalOpen(true); }}
                                        style={{ color: bloquearAjusteEnGlobal ? '#cbd5e1' : '#22BABB', fontWeight: '600' }}
                                    >
                                        Ajustar
                                    </Button>
                                </Tooltip>
                            )
                        }
                    ]}
                />
            </div>
        );
    };

    return (
        <ConfigProvider locale={es_ES}>
            <LayoutHome pageSeleccionada="inventario">
                <style>{`
                .scroll-inventario::-webkit-scrollbar { width: 6px; }
                .scroll-inventario::-webkit-scrollbar-track { background: transparent; }
                .scroll-inventario::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 10px; }
                .scroll-inventario::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
            `}</style>

                <div style={{ padding: isMobile ? '10px' : '16px 24px', background: '#fff', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ flexShrink: 0 }}>
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '15px' }}>

                                <Space direction="vertical" size={2}>
                                    <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>Gestión de Inventario</Title>

                                    {/* === NUEVO SELECTOR DE BODEGAS === */}
                                    {seccionActual === 'principal' && isPremium && (
                                        <Select
                                            value={bodegaActiva}
                                            onChange={setBodegaActiva}
                                            style={{ width: isMobile ? '100%' : 300, height: '40px' }}
                                            options={[
                                                { label: '🌎 Todas las Bodegas', value: 'global' },
                                                ...bodegasDB.filter(b => b.activa).map(b => ({ label: `📍 ${b.nombre}`, value: b.id }))
                                            ]}
                                        />
                                    )}
                                </Space>

                                <div style={{ background: '#f8fafc', padding: isMobile ? '16px' : '12px 20px', borderRadius: '16px', border: '1px solid #e2e8f0', width: isMobile ? '100%' : 'auto' }}>
                                    {isMobile ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', textAlign: 'center' }}>
                                                <div style={{ flex: 1 }}>
                                                    <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>INVERSIÓN (COSTO)</Text>
                                                    <Text strong style={{ fontSize: '16px', color: '#1e293b' }}>$ {statsGlobales.costoTotal.toLocaleString('es-CO')}</Text>
                                                </div>
                                                <div style={{ width: '1px', background: '#e2e8f0', margin: '0 10px' }}></div>
                                                <div style={{ flex: 1 }}>
                                                    <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>GANANCIA POTENCIAL</Text>
                                                    <Text strong style={{ fontSize: '16px', color: '#10b981' }}>+ $ {statsGlobales.gananciaBruta.toLocaleString('es-CO')}</Text>
                                                </div>
                                            </div>
                                            <Divider style={{ margin: '0' }} />
                                            <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                                                <div style={{ flex: 1 }}><Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>REFS</Text><Text strong style={{ fontSize: '14px' }}>{statsGlobales.refs}</Text></div>
                                                <div style={{ flex: 1, borderLeft: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0' }}><Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>{bodegaActiva === 'global' ? 'STOCK GLOBAL' : 'STOCK EN BODEGA'}</Text><Text strong style={{ fontSize: '14px' }}>{statsGlobales.stockTotal}</Text></div>
                                                <div style={{ flex: 1 }}><Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>MARGEN</Text><Text strong style={{ fontSize: '14px', color: '#10b981' }}>{statsGlobales.margenPorcentaje.toFixed(1)}%</Text></div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', gap: '25px', alignItems: 'center' }}>
                                            <Space direction="vertical" size={0} align="center"><Text type="secondary" style={{ fontSize: '10px' }}>REFS</Text><Text strong style={{ fontSize: '18px' }}>{statsGlobales.refs}</Text></Space>
                                            <Divider type="vertical" style={{ height: '30px' }} />
                                            <Space direction="vertical" size={0} align="center"><Text type="secondary" style={{ fontSize: '10px', color: bodegaActiva !== 'global' ? '#0284c7' : '#64748b' }}>{bodegaActiva === 'global' ? 'STOCK GLOBAL' : 'STOCK EN BODEGA'}</Text><Text strong style={{ fontSize: '18px', color: bodegaActiva !== 'global' ? '#0284c7' : '#0f172a' }}>{statsGlobales.stockTotal}</Text></Space>
                                            <Divider type="vertical" style={{ height: '30px' }} />
                                            <Space direction="vertical" size={0} align="center"><Text type="secondary" style={{ fontSize: '10px' }}>INVERSIÓN</Text><Text strong style={{ fontSize: '18px' }}>$ {statsGlobales.costoTotal.toLocaleString('es-CO')}</Text></Space>
                                            <Divider type="vertical" style={{ height: '30px' }} />
                                            <Space direction="vertical" size={0} align="center"><Text type="secondary" style={{ fontSize: '10px' }}>GANANCIA POTENCIAL</Text><Text strong style={{ fontSize: '18px', color: '#10b981' }}>+ $ {statsGlobales.gananciaBruta.toLocaleString('es-CO')}</Text></Space>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <Row gutter={[12, 12]} style={{ marginBottom: '16px' }} align="bottom">
                            <Col xs={24} md={seccionActual === 'trazabilidad' ? 8 : 12} lg={seccionActual === 'trazabilidad' ? 10 : 14}>
                                <Input
                                    placeholder="🔍 Buscar producto o código..."
                                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                    style={{ height: '40px', borderRadius: '8px' }}
                                    allowClear
                                    value={busqueda}
                                    onChange={(e) => setBusqueda(e.target.value)}
                                />
                            </Col>

                            {seccionActual === 'trazabilidad' && (
                                <Col xs={12} md={6} lg={4}>
                                    <Button
                                        type="default" block icon={<FilterOutlined />}
                                        style={{ height: '40px', borderRadius: '8px', borderColor: mostrarFiltros ? '#22BABB' : '#d9d9d9', color: mostrarFiltros ? '#22BABB' : 'rgba(0, 0, 0, 0.88)' }}
                                        onClick={() => setMostrarFiltros(!mostrarFiltros)}
                                    >Filtros</Button>
                                </Col>
                            )}

                            <Col xs={seccionActual === 'trazabilidad' ? 12 : 24} md={seccionActual === 'trazabilidad' ? 10 : 12} lg={seccionActual === 'trazabilidad' ? 10 : 10}>
                                <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '10px', width: '100%' }}>
                                    {[
                                        { id: 'principal', label: 'Existencias', icon: <ThunderboltOutlined /> },
                                        { id: 'trazabilidad', label: 'Historial', icon: <FileTextOutlined /> },
                                        ...(isPremium ? [{ id: 'bodegas', label: 'Bodegas', icon: <ShopOutlined /> }] : [])
                                    ].map((boton) => (
                                        <Button
                                            key={boton.id} type="text"
                                            onClick={() => { setSeccionActual(boton.id); setBodegaActiva('global'); }}
                                            style={{
                                                flex: 1, height: '32px', borderRadius: '8px', fontWeight: '700',
                                                backgroundColor: seccionActual === boton.id ? '#22BABB' : 'transparent',
                                                color: seccionActual === boton.id ? '#ffffff' : '#64748b'
                                            }}
                                            icon={boton.icon}
                                        >
                                            {boton.label}
                                        </Button>
                                    ))}
                                </div>
                            </Col>
                        </Row>

                        {mostrarFiltros && seccionActual === 'trazabilidad' && (
                            <div style={{ background: '#f8fafc', padding: '10px 16px', borderRadius: '12px', marginBottom: '12px', border: '1px solid #e2e8f0', animation: 'fadeIn 0.3s' }}>
                                <Row gutter={[12, 12]} align="bottom">
                                    <Col xs={24} md={10} lg={8}>
                                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                            <Text type="secondary" style={{ fontSize: '11px', fontWeight: '500' }}>Rango de búsqueda por fechas desde, hasta.</Text>
                                            <DatePicker.RangePicker
                                                value={[fechaDesde, fechaHasta]}
                                                onChange={(dates) => { if (dates) { setFechaDesde(dates[0]); setFechaHasta(dates[1]); } }}
                                                style={{ width: '100%', height: '36px', borderRadius: '8px' }} allowClear={false} format="DD/MM/YYYY"
                                            />
                                        </Space>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>

                    <div className="scroll-inventario" style={{ flex: 1, overflowY: (seccionActual === 'bodegas' || isMobile) ? 'auto' : 'hidden', minHeight: 0, paddingRight: isMobile ? '0' : '8px', paddingBottom: '10px' }}>
                        {seccionActual === 'principal' && (
                            isMobile ? (
                                <List
                                    dataSource={productosFiltrados}
                                    loading={cargandoProductos || cargandoStockBodega}
                                    renderItem={p => (
                                        <Card style={{ marginBottom: '12px', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <div style={{ flex: 1 }}>
                                                    <Text strong style={{ fontSize: '12px', display: 'block' }}>
                                                        {p.nombre.toUpperCase()} {p.es_combo && <Tag color="purple" style={{ fontSize: '10px', marginLeft: '5px' }}>Combo</Tag>}
                                                    </Text>
                                                    {!p.has_variants && <Text type="secondary" style={{ fontSize: '12px' }}>PVP: ${Number(p.precio_venta).toLocaleString('es-CO')}</Text>}
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    {p.es_combo ? (
                                                        p.stock > 0
                                                            ? <Tag color="purple" style={{ fontSize: '14px', fontWeight: 'bold', padding: '4px 12px', borderRadius: '8px' }}>Dinámico</Tag>
                                                            : <Tag color="red" style={{ fontSize: '14px', fontWeight: 'bold', padding: '4px 12px', borderRadius: '8px' }}>Agotado</Tag>
                                                    ) : (() => {
                                                        const stockReal = p.has_variants && p.variantes ? p.variantes.reduce((acumulado, vari) => acumulado + Number(vari.stock || 0), 0) : Number(p.stock || 0);
                                                        return <Tag color={stockReal <= (p.stock_minimo || 5) ? 'red' : 'blue'} style={{ fontSize: '14px', fontWeight: 'bold', padding: '4px 12px', borderRadius: '8px' }}>{stockReal} {p.has_variants && <span style={{ fontSize: '10px' }}>(Tot)</span>}</Tag>;
                                                    })()}
                                                </div>
                                            </div>
                                            <Divider style={{ margin: '12px 0' }} />

                                            {p.has_variants ? (
                                                <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '8px', borderLeft: '3px solid #bae6fd' }}>
                                                    <Text strong style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '8px' }}>PRESENTACIONES</Text>
                                                    {p.variantes?.map(v => (
                                                        <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e2e8f0' }}>
                                                            <div>
                                                                <Text strong style={{ fontSize: '13px', display: 'block' }}>{v.atributos?.nombre}</Text>
                                                                <Text type="secondary" style={{ fontSize: '11px' }}>Stock: {v.stock} | PVP: ${Number(v.precio_base).toLocaleString('es-CO')}</Text>
                                                            </div>
                                                            <Button size="small" type="link" icon={<EditOutlined />} disabled={bloquearAjusteEnGlobal} onClick={() => { setItemAjuste({ ...v, isVariant: true, producto_id: p.id, nombrePadre: p.nombre }); setIsModalOpen(true); }} style={{ color: bodegaActiva !== 'global' ? '#cbd5e1' : '#22BABB', fontWeight: '600' }}>Ajustar</Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : p.es_combo ? (
                                                <div style={{ textAlign: 'center', padding: '5px 0' }}><Text type="secondary" italic style={{ fontSize: '12px' }}>El inventario depende de los ingredientes.</Text></div>
                                            ) : (
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                    <Button type="primary" size="middle" icon={<EditOutlined />} disabled={bodegaActiva !== 'global'} onClick={() => { setItemAjuste(p); setIsModalOpen(true); }} style={{ background: bodegaActiva !== 'global' ? '#f1f5f9' : '#22BABB', color: bodegaActiva !== 'global' ? '#cbd5e1' : '#fff', borderRadius: '8px' }}>
                                                        Ajustar
                                                    </Button>
                                                </div>
                                            )}
                                        </Card>
                                    )}
                                />
                            ) : (
                                <Table
                                    size="small"
                                    columns={columnasWeb}
                                    dataSource={productosFiltrados}
                                    loading={cargandoProductos || cargandoStockBodega}
                                    rowKey="id"
                                    pagination={{ pageSize: 50 }}
                                    scroll={{ x: 'max-content', y: 'calc(100vh - 350px)' }}
                                    expandable={{
                                        expandedRowRender,
                                        rowExpandable: record => record.has_variants === true,
                                        expandIcon: ({ expanded, onExpand, record }) =>
                                            record.has_variants ? <Button type="text" icon={expanded ? <DownOutlined style={{ fontSize: '12px' }} /> : <RightOutlined style={{ fontSize: '12px' }} />} onClick={(e) => onExpand(record, e)} style={{ marginRight: 8, color: '#64748b' }} title='Ver variantes' /> : null
                                    }}
                                    style={{ border: '1px solid #f0f0f0', borderRadius: '12px', overflow: 'hidden' }}
                                />
                            )
                        )}

                        {seccionActual === 'trazabilidad' && (
                            isMobile ? (
                                <List dataSource={historialSoloManual} renderItem={h => (
                                    <Card style={{ marginBottom: '10px', borderRadius: '14px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text strong>{h.producto_nombre?.toUpperCase()}</Text>
                                            <Tag color={h.tipo_movimiento === 'entrada' ? 'green' : h.tipo_movimiento === 'salida' ? 'red' : 'blue'}>{h.tipo_movimiento.toUpperCase().replace('_', ' ')}</Tag>
                                        </div>
                                        <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                            <Text type="secondary" style={{ fontSize: '11px' }}><HistoryOutlined /> {dayjs(h.fecha).format('DD/MM/YY hh:mm A')}</Text>
                                            <Text type="secondary" style={{ fontSize: '11px' }}><UserOutlined /> {h.usuario_nombre ? `${h.usuario_nombre} ${h.usuario_apellidos || ''}` : 'Sistema'}</Text>
                                        </div>
                                        {h.observaciones && <div style={{ marginTop: '10px', padding: '8px', background: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #22BABB' }}><Text style={{ fontSize: '12px' }}>{h.observaciones}</Text></div>}
                                    </Card>
                                )} />
                            ) : (
                                <Table
                                    size="small" pagination={{ pageSize: 50 }}
                                    scroll={{ x: 'max-content', y: mostrarFiltros ? 'calc(100vh - 423px)' : 'calc(100vh - 330px)' }}
                                    columns={[
                                        { title: 'FECHA DEL MOVIMIENTO', dataIndex: 'fecha', width: '15%', render: f => dayjs(f).format('DD/MM/YYYY hh:mm A') },
                                        { title: 'TIPO', width: '7%', dataIndex: 'tipo_movimiento', render: t => <Tag color={t === 'entrada' ? 'green' : t === 'salida' ? 'red' : 'blue'}>{t.toUpperCase().replace('_', ' ')}</Tag> },
                                        { title: 'POR EL USUARIO', width: '15%', render: (_, r) => <Text><UserOutlined /> {r.usuario_nombre ? `${r.usuario_nombre} ${r.usuario_apellidos}` : 'Sistema'}</Text> },
                                        { title: 'PRODUCTO ó VARIANTE', width: '30%', dataIndex: 'producto_nombre', render: t => <Text strong>{t?.toUpperCase()}</Text> },
                                        { title: 'DETALLE DEL CAMBIO', width: '33%', dataIndex: 'observaciones', render: obs => <Text>{obs}</Text> }
                                    ]}
                                    dataSource={historialSoloManual} loading={cargandoHistorial} rowKey="id"
                                />
                            )
                        )}

                        {seccionActual === 'bodegas' && isPremium && (
                            <div style={{ padding: '0', animation: 'fadeIn 0.3s' }}>
                                <TabBodegas />
                            </div>
                        )}
                    </div>
                </div>

                {/* MODAL DE AJUSTES (Permanece igual) */}
                <Modal
                    title={null} open={isModalOpen} onCancel={() => !ajustarInventario.isPending && !ajustarInventario.isLoading && setIsModalOpen(false)} footer={null} centered width={600}
                    closable={!ajustarInventario.isPending && !ajustarInventario.isLoading}
                    maskClosable={!ajustarInventario.isPending && !ajustarInventario.isLoading}
                    styles={{ mask: { background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(5px)' } }}
                >
                    <div style={{ textAlign: 'center', padding: '10px 0 20px 0' }}>
                        <Title level={4} style={{ margin: 0 }}>{itemAjuste?.isVariant ? 'Actualizar Variante' : 'Actualizar Producto'}</Title>
                        <Text type="secondary" style={{ fontSize: '16px', display: 'block' }}>
                            {itemAjuste?.isVariant ? `${itemAjuste.nombrePadre} - ${itemAjuste.atributos?.nombre}` : itemAjuste?.nombre?.toUpperCase()}
                        </Text>
                        <Tag color="cyan" style={{ marginTop: 8, fontSize: '12px', padding: '2px 10px', borderRadius: '6px' }}>
                            📍 Afectando: <b>{nombreBodegaAjuste}</b>
                        </Tag>
                    </div>

                    <Form layout="vertical" form={form} onValuesChange={handleFormValuesChange} onFinish={(v) => {
                        let stockFinal = itemAjuste?.stock || 0;
                        const cantidadDigitada = Number(v.cantidad_ajuste) || 0;

                        if (v.tipo_ajuste === 'agregar') stockFinal += cantidadDigitada;
                        if (v.tipo_ajuste === 'restar') stockFinal = Math.max(0, stockFinal - cantidadDigitada);
                        if (v.tipo_ajuste === 'reemplazar') stockFinal = cantidadDigitada;

                        const bodegaDestinoId = bodegaActiva === 'global'
                            ? bodegasDB.find(b => b.es_default)?.id
                            : bodegaActiva;

                        ajustarInventario.mutate({
                            producto_id: itemAjuste.isVariant ? itemAjuste.producto_id : itemAjuste.id,
                            variante_id: itemAjuste.isVariant ? itemAjuste.id : null,
                            nombre_variante: itemAjuste.isVariant ? itemAjuste.atributos?.nombre : null,
                            bodega_id: bodegaDestinoId, // <-- ENVIAMOS LA BODEGA EXACTA
                            nuevo_stock: stockFinal,
                            nuevo_precio_compra: v.nuevo_precio_compra,
                            nuevo_precio_venta: v.nuevo_precio_venta,
                            observaciones: v.observaciones
                        }, {
                            onSuccess: () => {
                                message.success(`Ajuste aplicado en ${nombreBodegaAjuste}`);
                                setIsModalOpen(false);
                                form.resetFields();
                            }
                        });
                    }}>
                        <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                            <Row gutter={16} align="bottom">
                                <Col xs={24}>
                                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>Movimiento del Stock</Text>
                                    <Form.Item name="tipo_ajuste" style={{ marginBottom: '12px' }}>
                                        <Radio.Group optionType="button" buttonStyle="solid" style={{ width: '100%', display: 'flex' }}>
                                            <Radio value="agregar" style={{ flex: 1, textAlign: 'center' }} title='Agregar al stock actual'>+ Agregar</Radio>
                                            <Radio value="restar" style={{ flex: 1, textAlign: 'center' }} title='Restar del stock actual'>Restar</Radio>
                                            <Radio value="reemplazar" style={{ flex: 1, textAlign: 'center' }} title='Colocar como nuevo stock'>Exacto</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={10}>
                                    <Form.Item name="cantidad_ajuste" style={{ marginBottom: 0 }}>
                                        <InputNumber controls={false} style={{ width: '100%' }} min={0} size="large" placeholder="Cantidad" onFocus={(e) => e.target.select()} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={14}>
                                    <Alert type="info" showIcon={false} style={{ padding: '7px 15px', background: '#e0f2fe', border: 'none', borderRadius: '8px' }}
                                        message={
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                                                <span style={{ color: '#0369a1' }}>Stock actual: <b>{itemAjuste?.stock || 0}</b></span>
                                                <ArrowRightOutlined style={{ color: '#0284c7' }} />
                                                <span style={{ color: '#0284c7' }}>Quedará en: <Tag color="blue" style={{ margin: 0, fontWeight: 'bold' }}>{previewStock}</Tag></span>
                                            </div>
                                        }
                                    />
                                </Col>
                            </Row>

                            <Divider style={{ margin: '16px 0' }} />

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item name="nuevo_precio_compra" label={<Text strong>Costo Unitario ($)</Text>} rules={[{ required: true, message: 'Requerido' }]} style={{ marginBottom: 0 }}>
                                        <InputNumber style={{ width: '100%' }} size="large" min={0} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item name="nuevo_precio_venta" label={<Text strong>Precio Venta ($)</Text>} rules={[{ required: true, message: 'Requerido' }]} style={{ marginBottom: 0 }}>
                                        <InputNumber style={{ width: '100%' }} size="large" min={0} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>

                        <Form.Item name="observaciones" label={<Text strong>Observaciones</Text>} rules={[{ required: true, message: 'Ingrese la observación.' }]}>
                            <Input.TextArea rows={2} placeholder="Ej: Ingreso de mercancía, merma por daño..." />
                        </Form.Item>

                        <Button type="primary" htmlType="submit" block size="large" loading={ajustarInventario.isLoading || ajustarInventario.isPending} disabled={ajustarInventario.isLoading || ajustarInventario.isPending} style={{ background: '#22BABB', height: '50px', borderRadius: '12px', fontWeight: 'bold' }}>
                            {(ajustarInventario.isLoading || ajustarInventario.isPending) ? 'GUARDANDO...' : 'GUARDAR CAMBIOS'}
                        </Button>
                    </Form>
                </Modal>
            </LayoutHome>
        </ConfigProvider>
    );
};

export default Inventario;